import React, { useEffect, useState } from 'react'

import { Redirect, useParams } from 'react-router-dom'

import { useIntegrationActions } from 'redux/actions/Integrations'
import {
  useFortnoxStatusFetched,
  useFortnoxTenantStatus,
  useIntegrations,
  useIntegrationsFetched
} from 'redux/hooks/Integrations'

import { ChipProps } from 'components_new/atoms/Chip'
import { EFortnoxTenantStatus } from 'types/Enums'
import { FortnoxTenantStatus } from 'types/GlobalIntegration'
import { useTranslation } from 'language/translator'
import { parseDateString } from 'helpers/Functions/Date'

import Box from 'components_new/atoms/Box'
import Button from 'components_new/atoms/Button'
import Chip from 'components_new/atoms/Chip'
import Icon from 'components_new/atoms/Icon'
import IconButton from 'components_new/atoms/IconButton'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import SectionHeader from 'components_new/molecules/SectionHeader'
import SimpleTable from 'components_new/molecules/SimpleTable'

import DeleteDialog from 'components_new/organisms/dialogs/DeleteDialog'

import PageLayout from 'components_new/templates/PageLayout'

// Re-authorization only helps for NEEDS_REAUTH, so the reconnect action is
// limited to that status.
const RECONNECTABLE_STATUS = EFortnoxTenantStatus.NEEDS_REAUTH

const STATUS_COLORS: { [key in EFortnoxTenantStatus]: ChipProps['color'] } = {
  [EFortnoxTenantStatus.OK]: 'success',
  [EFortnoxTenantStatus.NEEDS_REAUTH]: 'error',
  [EFortnoxTenantStatus.MISSING_LICENSE]: 'warning'
}

const STATUS_LABEL_KEYS: {
  [key in EFortnoxTenantStatus]:
    | 'status_ok'
    | 'status_needs_reauth'
    | 'status_missing_license'
} = {
  [EFortnoxTenantStatus.OK]: 'status_ok',
  [EFortnoxTenantStatus.NEEDS_REAUTH]: 'status_needs_reauth',
  [EFortnoxTenantStatus.MISSING_LICENSE]: 'status_missing_license'
}

const getStatusColor = (status: EFortnoxTenantStatus): ChipProps['color'] =>
  STATUS_COLORS[status] ?? 'default'

const formatDate = (value: string | null) => {
  if (!value) {
    return '-'
  }

  return parseDateString(value)
}

const FortnoxDetail = () => {
  const {
    tryGetIntegrations,
    tryGetFortnoxTenantStatus,
    tryReconnectFortnox,
    tryRemoveFortnoxTenant
  } = useIntegrationActions()

  const integrations = useIntegrations()
  const integrationsFetched = useIntegrationsFetched()
  const tenants = useFortnoxTenantStatus()
  const statusFetched = useFortnoxStatusFetched()

  const { t } = useTranslation()
  const { type } = useParams<{ type: string }>()

  const [tenantToRemove, setTenantToRemove] =
    useState<FortnoxTenantStatus | null>(null)

  useEffect(() => {
    if (!statusFetched) {
      tryGetFortnoxTenantStatus()
    }

    if (!integrationsFetched) {
      tryGetIntegrations()
    }
  }, [])

  if (type !== 'fortnox') {
    return <Redirect to="/settings/integrations" />
  }

  const loading = !statusFetched

  // Active source to authorize against. New tenants derive their id from the
  // resulting token, so any of the customer's Fortnox active sources works.
  const fortnoxIntegration = integrations.find(
    (integration) => integration.key === 'fortnox'
  )
  const authorizeActiveSourceId =
    fortnoxIntegration?.activeSourceIds[0] ?? tenants[0]?.activeSourceId

  const getStatusLabel = (status: EFortnoxTenantStatus) => {
    const key = STATUS_LABEL_KEYS[status]

    return key ? t('integrations', key) : status
  }

  return (
    <PageLayout
      topbar={{
        breadcrumbs: [
          { href: '/settings', title: t('global', 'settings') },
          {
            href: '/settings/integrations',
            title: t('global', 'integrations')
          },
          { title: t('integrations', 'fortnox_page_title') }
        ]
      }}
      content={{ size: 'large' }}
      variant="feed"
    >
      <SectionHeader
        actions={
          <Button
            disabled={!authorizeActiveSourceId}
            onClick={() =>
              authorizeActiveSourceId &&
              tryReconnectFortnox(authorizeActiveSourceId)
            }
            startIcon={<Icon name="Add" fontSize="small" />}
            variant="contained"
          >
            {t('integrations', 'authenticate_new_tenant')}
          </Button>
        }
        body={t('integrations', 'fortnox_page_body')}
        size="large"
        sx={{ mb: 2 }}
        title={t('integrations', 'fortnox_page_title')}
      />

      <SimpleTable
        border={true}
        columns={[
          { title: t('integrations', 'tenant_name'), width: 250 },
          { title: t('integrations', 'organization_number') },
          { title: t('integrations', 'status') },
          { title: t('integrations', 'message') },
          { title: t('integrations', 'last_successful_run') },
          { title: t('integrations', 'last_checked_at') },
          { align: 'right' as const }
        ]}
        loading={loading}
        rows={tenants.map((tenant) => ({
          id: `${tenant.activeSourceId}:${tenant.tenantId}`,
          cells: [
            <Text key={0}>{tenant.name ?? tenant.tenantId}</Text>,
            <Text key={1} color="text.secondary">
              {tenant.organizationNumber ?? '-'}
            </Text>,
            <Chip
              key={2}
              color={getStatusColor(tenant.status)}
              label={getStatusLabel(tenant.status)}
              size="small"
            />,
            <Text key={3} color="text.secondary">
              {tenant.message ?? '-'}
            </Text>,
            <Text key={4} color="text.secondary">
              {formatDate(tenant.lastSuccessfulRun)}
            </Text>,
            <Text key={5} color="text.secondary">
              {formatDate(tenant.lastCheckedAt)}
            </Text>,
            <Box
              key={6}
              sx={{
                display: 'flex',
                gap: 1,
                justifyContent: 'flex-end',
                alignItems: 'center'
              }}
            >
              {tenant.status === RECONNECTABLE_STATUS ? (
                <Button
                  onClick={() => tryReconnectFortnox(tenant.activeSourceId)}
                  size="small"
                  startIcon={<Icon name="Sync" fontSize="small" />}
                  variant="outlined"
                >
                  {t('integrations', 'reconnect')}
                </Button>
              ) : null}
              <Tooltip title={t('integrations', 'remove_tenant')}>
                <IconButton
                  onClick={() => setTenantToRemove(tenant)}
                  size="small"
                >
                  <Icon name="Delete" fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          ]
        }))}
      />

      <DeleteDialog
        onClose={() => setTenantToRemove(null)}
        onDelete={(callback) => {
          if (tenantToRemove) {
            tryRemoveFortnoxTenant(
              tenantToRemove.activeSourceId,
              tenantToRemove.tenantId
            )
          }

          callback()
          setTenantToRemove(null)
        }}
        open={Boolean(tenantToRemove)}
        title={tenantToRemove?.name ?? tenantToRemove?.tenantId ?? ''}
        type={t('integrations', 'tenant_name', true)}
      />
    </PageLayout>
  )
}

export default FortnoxDetail
